import {Component, Input, OnInit} from '@angular/core';
import {NbDialogRef} from '@nebular/theme';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {debounceTime, tap} from 'rxjs/operators';
import moment from 'moment';
import {ProjectService} from '../../shared/project.service';
import {SubscribedComponent} from '../../subscribed.component';
import {HoursOverviewPreview} from '../../interfaces/hoursOverview';
import {TranslateService} from '@ngx-translate/core';

/** Approved hours of one client project over a period, with a PDF download (#1493). */
@Component({
    selector: 'bst-hours-overview-dialog',
    templateUrl: './hours-overview-dialog.component.html',
    styleUrls: ['./hours-overview-dialog.component.scss'],
    standalone: false
})
export class HoursOverviewDialogComponent extends SubscribedComponent implements OnInit {

  @Input() clientId: number;
  @Input() projectId: number;
  @Input() projectName: string;

  public form: FormGroup;
  public preview: HoursOverviewPreview;
  public busy = false;
  public failed = false;
  private seq = 0;
  public downloading = false;
  /** Km go on the PDF when the period has chargeable trips, unless unticked (#1621). */
  public includeKm = true;

  constructor(protected ref: NbDialogRef<HoursOverviewDialogComponent>,
              private fb: FormBuilder,
              private projectService: ProjectService,
              private translate: TranslateService) {
    super();
    const lastMonth = moment().subtract(1, 'month');
    this.form = this.fb.group({
      from: [lastMonth.clone().startOf('month'), Validators.required],
      to: [lastMonth.clone().endOf('month'), Validators.required],
    });
  }

  ngOnInit(): void {
    // Drop the old period's preview at once, so Download never pairs new dates with it.
    this.subs.push(this.form.valueChanges.pipe(
      tap(() => { this.seq++; this.preview = null; }),
      debounceTime(300),
    ).subscribe(() => this.load()));
    this.load();
  }

  // The UI language, passed to the number and currency pipes: LOCALE_ID is fixed at bootstrap.
  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  /** Some total has an amount; no_rate lists names, and two people can share one. */
  get priced(): boolean{
    return this.preview.totals.some(t => t.amount_cents != null);
  }

  /** Trips are on for the tenant and the period has chargeable ones. */
  get hasKm(): boolean{
    return !!this.preview?.km?.totals.length;
  }

  get withKm(): boolean{
    return this.hasKm && this.includeKm;
  }

  get blocked(): boolean{
    return !this.preview || this.preview.no_rate.length > 0 || (this.withKm && this.preview.km.no_price.length > 0);
  }

  get valid(): boolean{
    const {from, to} = this.form.value;
    return this.form.valid && !moment(to).isBefore(from, 'day');
  }

  async load(){
    // Only the latest request may set the preview; an older, slower one is dropped.
    const seq = ++this.seq;
    this.preview = null;
    this.busy = false;
    this.failed = false;
    if(!this.valid){
      return;
    }
    const {from, to} = this.form.value;
    this.busy = true;
    try {
      const preview = await this.projectService.hoursOverview(this.clientId, this.projectId, from, to);
      if(seq === this.seq){
        this.preview = preview;
      }
    } catch {
      if(seq === this.seq){
        this.failed = true;
      }
    } finally {
      if(seq === this.seq){
        this.busy = false;
      }
    }
  }

  async download(){
    if(!this.valid || this.downloading || this.blocked){
      return;
    }
    const {from, to} = this.form.value;
    this.downloading = true;
    try {
      await this.projectService.downloadHoursOverview(this.clientId, this.projectId, from, to, this.withKm);
    } finally {
      this.downloading = false;
    }
  }

  close(){
    this.ref.close();
  }
}
